import { AssociationRecord } from "@/shared/domain/association.ts";
import { type CustomFieldChangeset } from "@/shared/domain/customFields";
import { ISO_8601_DATETIME } from "@/types";

/**
 * A field of a topic a session event is about. Its ID belongs to one of the
 * topic's schemas, so it matches the field only in data written with that schema.
 */
export interface EventTopicField {
  field_id: string;
  /** The field's path within its schema, for display. */
  path_in_schema: string[];
}

/** A topic a session event is about, and optionally its fields. */
export interface EventTopic {
  topic_id: string;
  topic_name: string;
  /** The fields, or null for the whole topic. */
  fields: EventTopicField[] | null;
}

/**
 * An event on a dataset or a session. Events on a dataset carry
 * `associations`; events on a session carry `session_id` and `topics` instead,
 * and their `associations` are always empty (see `normalizeEventRecord`).
 */
export interface EventRecord {
  associations: AssociationRecord[];
  /** Set only on an event that belongs to a session. */
  session_id?: string;
  /** The topics a session event is about; empty means all of its session's data. */
  topics?: EventTopic[];
  created: ISO_8601_DATETIME;
  created_by: string;
  description: string;
  display_options: EventDisplayOptions | null;
  end_time: bigint;
  event_id: string;
  name: string;
  metadata: Record<string, unknown>;
  custom_fields: Record<string, unknown>;
  modified: ISO_8601_DATETIME;
  modified_by: string;
  org_id: string;
  start_time: bigint;
  tags: string[];
}

/** Session events have `evt_` IDs; dataset events have `ev_`. */
const SESSION_EVENT_ID_PREFIX = "evt_";

/** Whether `eventId` names an event on a session. */
export function isSessionEventId(eventId: string): boolean {
  return eventId.startsWith(SESSION_EVENT_ID_PREFIX);
}

/**
 * Fill in an empty `associations` list on a session event's record, which the
 * API sends without that field, so code that reads associations treats the
 * event as attached to none. A dataset event's associations pass through.
 */
export function normalizeEventRecord(
  record: Omit<EventRecord, "associations"> & {
    associations?: AssociationRecord[];
  },
): EventRecord {
  return { ...record, associations: record.associations ?? [] };
}

export interface CreateEventRequest {
  associations: AssociationRecord[];
  /** Initial values for Ready custom fields on the new event, by field name. */
  custom_fields?: Record<string, unknown>;
  description?: string;
  end_time: bigint;
  name: string;
  metadata: Record<string, unknown>;
  start_time: bigint;
  tags: string[];
  /** Optional display options (e.g., custom color). Omit to use auto-generated colors. */
  display_options?: EventDisplayOptions;
}

/**
 * Request body for `POST /v2/events`: an event on one session. An empty
 * `topics` makes the event about all of the session's data. A topic entry
 * names fields by schema field ID (`sf_…`).
 */
export interface CreateSessionEventRequest {
  name: string;
  session_id: string;
  start_time: bigint;
  end_time: bigint;
  /** Omit `field_ids` for the whole topic; the API rejects an empty list. */
  topics: { topic_id: string; field_ids?: string[] }[];
  description?: string;
  tags: string[];
  metadata: Record<string, unknown>;
  display_options?: EventDisplayOptions;
  /** Initial values for Ready custom fields on the new event, by field name. */
  custom_fields?: Record<string, unknown>;
}

export interface EventDisplayOptions {
  color?: string;
}

export interface QueryEventsForAssociationsRequest {
  associations: AssociationRecord[];
  page_token?: string;
}

export interface DeleteEventsRequest {
  event_ids: string[];
}

export interface UpdateEventRequest {
  name?: string;
  description?: string;
  metadata_changeset?: {
    put_fields?: Record<string, unknown>;
    remove_fields?: string[];
    put_tags?: string[];
    remove_tags?: string[];
  };
  /** Changeset for updating display options. Set `color` to a string to update, `null` to clear. */
  display_options_changeset?: EventDisplayOptionsChangeset;
  /** Set/clear Ready custom-field values; absent = no change. */
  custom_fields_changeset?: CustomFieldChangeset;
}

/** Changeset for event display options. Use `null` to clear a custom color. */
export interface EventDisplayOptionsChangeset {
  color?: string | null;
}
